import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Instagram, Facebook, Linkedin, Youtube, ArrowRight, CheckCircle } from "lucide-react";
import { API } from "../../apiConfig";
import { useSocialLinks } from "../../hooks/useSocialLinks";

export const Footer = () => {
  const [cmsContent, setCmsContent] = useState({});
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    fetchCmsContent();
  }, []);

  const fetchCmsContent = async () => {
    try {
      const res = await axios.get(`${API}/cms/footer`);
      const cmsObj = {};
      res.data.forEach((item) => {
        cmsObj[item.section] = item.content;
      });
      setCmsContent(cmsObj);
    } catch (error) {
      console.error("Error fetching footer CMS:", error);
    }
  };

  const cms = (key, fallback) => cmsContent[key] || fallback;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setIsSubscribed(true);
      setEmail("");
      toast.success("Welcome to the Arawol community!");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("You're already subscribed!");
        setIsSubscribed(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const social = useSocialLinks();

  const socialLinks = [
    { icon: Instagram, href: social.instagram, label: "Instagram" },
    { icon: Facebook, href: social.facebook, label: "Facebook" },
    { icon: Linkedin, href: social.linkedin, label: "LinkedIn" },
    { icon: Youtube, href: social.youtube, label: "YouTube" },
  ].filter((s) => s.href);

  const exploreLinks = [
    { to: "/artist", label: "The Artist" },
    { to: "/the-collection", label: "The Collection" },
    { to: "/commissions", label: "Commissions" },
    { to: "/auctions", label: "Silent Auctions" },
  ];

  const supportLinks = [
    { to: "/quote-request", label: "Trade Enquiries" },
    { to: "/shipping", label: "Shipping Information" },
    { to: "/refund-policy", label: "Returns & Refunds" },
    { to: "/faq", label: "FAQ" },
    { to: "/register?role=promoter", label: "Become an Ambassador" },
  ];

  return (
    <footer className="bg-[#141210] text-[#F5F3EF]" data-testid="footer">
      {/* Colophon masthead */}
      <div className="max-w-7xl mx-auto px-6 md:px-16 pt-24 md:pt-32">
        <div className="flex items-center justify-between border-b border-white/15 pb-5 mb-14">
          <p className="folio text-white/40">Art By Arawol — Colophon</p>
          <p className="plate-no text-white/30 hidden md:block">Gadigal Country, Australia</p>
        </div>

        <h2 className="text-4xl sm:text-5xl md:text-7xl font-light leading-[1.02] max-w-4xl mb-16">
          <em>Art from Country.</em>
          <br />
          For Country.
        </h2>

        {/* Newsletter */}
        <div className="border-t border-white/15 py-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="text-2xl md:text-3xl font-light mb-3">
              <em>The Collector's Circle</em>
            </h3>
            <p className="text-white/50 max-w-md text-sm leading-relaxed">
              Be first to know about new artworks, exclusive releases & Colin's journey back to Country.
            </p>
          </div>
          {isSubscribed ? (
            <div className="flex items-center gap-3 text-emerald-400 md:justify-end" data-testid="newsletter-subscribed">
              <CheckCircle className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">You're on the list.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex w-full gap-3 md:justify-end">
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="md:w-80 h-12 rounded-full bg-white/5 border-white/20 text-white placeholder:text-white/35 focus-visible:ring-white/30"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-12 px-7 rounded-full bg-[#F5F3EF] text-[#141210] hover:bg-white"
                data-testid="newsletter-subscribe-btn"
              >
                {isSubmitting ? "..." : "Subscribe"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
        </div>

        {/* Link columns */}
        <div className="border-t border-white/15 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_8576cbf0-8d30-4753-9193-cebdf96ed739/artifacts/m5p2j94m_Asset%205.png"
                alt="Wallaby"
                className="h-10 w-auto"
              />
              <span className="font-brand text-lg text-white">Arawol</span>
            </div>
            <p className="text-white/50 leading-relaxed max-w-md text-sm">
              {cms(
                "tagline",
                "Authentic Aboriginal art by Elder Colin Isaacs, connecting collectors with 65,000+ years of continuous culture."
              )}
            </p>
            <div className="flex gap-3 mt-8">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-full border border-white/20 hover:bg-white hover:text-[#141210] flex items-center justify-center transition-colors duration-300"
                  aria-label={social.label}
                  data-testid={`social-link-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="folio text-white/40 mb-6">Explore</h4>
            <ul className="space-y-4">
              {exploreLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/70 hover:text-white transition-colors text-sm">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="folio text-white/40 mb-6">Enquiries</h4>
            <ul className="space-y-4">
              {supportLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/70 hover:text-white transition-colors text-sm">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/15 py-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-xs text-white/40">
            {cms("copyright", `© ${new Date().getFullYear()} Arawol Pty Ltd. All rights reserved.`)}
          </p>
          <p className="font-accent italic text-white/60 text-center">
            {cms("acknowledgement", "We acknowledge the Traditional Owners of the land on which we work")}
          </p>
          <div className="flex flex-wrap justify-center gap-5 text-xs text-white/40">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/shipping" className="hover:text-white transition-colors">Shipping</Link>
            <Link to="/login" className="hover:text-white transition-colors" data-testid="footer-gallery-signin">
              Gallery Sign In
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
